import type { AssistantGraphVersionPins } from "@/lib/ai/agent-graph/structured-provider-contracts";
import type { AssistantTask } from "@/lib/ai/agent-graph/contracts";

export const ASSISTANT_RESPONSE_DELIVERY_VERSION = "assistant-response-delivery.v1" as const;
export const ASSISTANT_FEEDBACK_VERSION = "assistant-feedback.v1" as const;

export const ASSISTANT_FEEDBACK_RATINGS = ["helpful", "not_helpful"] as const;
export type AssistantFeedbackRating = (typeof ASSISTANT_FEEDBACK_RATINGS)[number];
export type AssistantMeasuredTask = AssistantTask | "legacy" | "navigation";

export const LEGACY_ASSISTANT_VERSION_PINS = Object.freeze({
    graph: "legacy-assistant.v1",
    routerPrompt: "legacy.not_applicable.v1",
    responsePrompt: "legacy-assistant-prompt.v1",
    verifierPrompt: "legacy.not_applicable.v1",
    routerModel: "legacy.not_applicable.v1",
    responseModel: "legacy-model.unknown.v1",
    verifierModel: "legacy.not_applicable.v1",
    toolSchema: "legacy-assistant-tools.v1",
    craftRubric: "legacy.not_applicable.v1",
    retrievalIndex: "legacy-assistant-retrieval.v1",
    trace: "legacy.not_applicable.v1",
});

export interface AssistantResponseDeliveryMetadata {
    readonly version: typeof ASSISTANT_RESPONSE_DELIVERY_VERSION;
    readonly experience: "legacy" | "v2";
    readonly responseKind: "legacy_response" | "navigation" | "final_response";
    readonly task: AssistantMeasuredTask;
    readonly graphVersion: string;
    readonly routerPromptVersion: string;
    readonly responsePromptVersion: string;
    readonly verifierPromptVersion: string;
    readonly routerModel: string;
    readonly responseModel: string;
    readonly verifierModel: string;
    readonly toolSchemaVersion: string;
    readonly craftRubricVersion: string;
    readonly retrievalIndexVersion: string;
    readonly traceVersion: string;
    readonly evidenceIds: readonly string[];
}

export interface AssistantFeedbackInput {
    readonly version: typeof ASSISTANT_FEEDBACK_VERSION;
    readonly responseId: string;
    readonly rating: AssistantFeedbackRating;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const EVIDENCE_ID_RE = /^evidence\.v1\.[a-z_]+\.[a-f0-9]{64}$/u;
const VERSION_RE = /^[a-z0-9][a-z0-9._:-]{0,159}$/iu;
const DELIVERY_KEYS = Object.freeze([
    "craftRubricVersion",
    "evidenceIds",
    "experience",
    "graphVersion",
    "responseKind",
    "responsePromptVersion",
    "responseModel",
    "retrievalIndexVersion",
    "routerModel",
    "routerPromptVersion",
    "task",
    "toolSchemaVersion",
    "traceVersion",
    "verifierModel",
    "verifierPromptVersion",
    "version",
]);
const FEEDBACK_KEYS = Object.freeze([
    "rating",
    "responseId",
    "version",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
    const actual = Object.keys(value).sort();
    const wanted = [...expected].sort();
    return actual.length === wanted.length && actual.every((key, index) => key === wanted[index]);
}

function validVersion(value: unknown): value is string {
    return typeof value === "string" && VERSION_RE.test(value);
}

function validEvidenceIds(value: unknown): value is string[] {
    return (
        Array.isArray(value) &&
        value.length <= 64 &&
        value.every((id) => typeof id === "string" && EVIDENCE_ID_RE.test(id)) &&
        new Set(value).size === value.length
    );
}

function parseDeliveryRecord(
    value: Record<string, unknown>,
): Readonly<AssistantResponseDeliveryMetadata> | null {
    if (
        value.version !== ASSISTANT_RESPONSE_DELIVERY_VERSION ||
        (value.experience !== "legacy" && value.experience !== "v2") ||
        (value.responseKind !== "legacy_response" &&
            value.responseKind !== "navigation" &&
            value.responseKind !== "final_response") ||
        (value.task !== "legacy" &&
            value.task !== "navigation" &&
            value.task !== "coach" &&
            value.task !== "assist" &&
            value.task !== "story_record" &&
            value.task !== "clarify") ||
        !validVersion(value.graphVersion) ||
        !validVersion(value.routerPromptVersion) ||
        !validVersion(value.responsePromptVersion) ||
        !validVersion(value.verifierPromptVersion) ||
        !validVersion(value.routerModel) ||
        !validVersion(value.responseModel) ||
        !validVersion(value.verifierModel) ||
        !validVersion(value.toolSchemaVersion) ||
        !validVersion(value.craftRubricVersion) ||
        !validVersion(value.retrievalIndexVersion) ||
        !validVersion(value.traceVersion) ||
        !validEvidenceIds(value.evidenceIds)
    ) {
        return null;
    }
    if (value.experience === "legacy" && value.responseKind !== "legacy_response") return null;
    if (value.experience === "v2" && value.responseKind === "legacy_response") return null;
    if (value.experience === "legacy" && value.task !== "legacy") return null;
    if (value.responseKind === "navigation" && value.task !== "navigation") return null;
    if (value.responseKind === "final_response" && (value.task === "legacy" || value.task === "navigation")) return null;
    return Object.freeze({
        version: ASSISTANT_RESPONSE_DELIVERY_VERSION,
        experience: value.experience,
        responseKind: value.responseKind,
        task: value.task,
        graphVersion: value.graphVersion,
        routerPromptVersion: value.routerPromptVersion,
        responsePromptVersion: value.responsePromptVersion,
        verifierPromptVersion: value.verifierPromptVersion,
        routerModel: value.routerModel,
        responseModel: value.responseModel,
        verifierModel: value.verifierModel,
        toolSchemaVersion: value.toolSchemaVersion,
        craftRubricVersion: value.craftRubricVersion,
        retrievalIndexVersion: value.retrievalIndexVersion,
        traceVersion: value.traceVersion,
        evidenceIds: Object.freeze([...value.evidenceIds]),
    });
}

export function parseAssistantResponseDeliveryMetadata(
    value: unknown,
): Readonly<AssistantResponseDeliveryMetadata> | null {
    if (!isRecord(value) || !hasExactKeys(value, DELIVERY_KEYS)) return null;
    return parseDeliveryRecord(value);
}

export function parseAssistantFeedbackInput(
    value: unknown,
): Readonly<AssistantFeedbackInput> | null {
    if (!isRecord(value) || !hasExactKeys(value, FEEDBACK_KEYS)) return null;
    if (
        value.version !== ASSISTANT_FEEDBACK_VERSION ||
        !UUID_RE.test(String(value.responseId ?? "")) ||
        (value.rating !== "helpful" && value.rating !== "not_helpful")
    ) {
        return null;
    }
    return Object.freeze({
        version: ASSISTANT_FEEDBACK_VERSION,
        responseId: value.responseId as string,
        rating: value.rating,
    });
}

function metadataFromPins(
    experience: "legacy" | "v2",
    responseKind: AssistantResponseDeliveryMetadata["responseKind"],
    task: AssistantMeasuredTask,
    pins: Readonly<{
        graph: string;
        routerPrompt: string;
        responsePrompt: string;
        verifierPrompt: string;
        routerModel: string;
        responseModel: string;
        verifierModel: string;
        toolSchema: string;
        craftRubric: string;
        retrievalIndex: string;
        trace: string;
    }>,
    evidenceIds: readonly string[],
): Readonly<AssistantResponseDeliveryMetadata> {
    const parsed = parseAssistantResponseDeliveryMetadata({
        version: ASSISTANT_RESPONSE_DELIVERY_VERSION,
        experience,
        responseKind,
        task,
        graphVersion: pins.graph,
        routerPromptVersion: pins.routerPrompt,
        responsePromptVersion: pins.responsePrompt,
        verifierPromptVersion: pins.verifierPrompt,
        routerModel: pins.routerModel,
        responseModel: pins.responseModel,
        verifierModel: pins.verifierModel,
        toolSchemaVersion: pins.toolSchema,
        craftRubricVersion: pins.craftRubric,
        retrievalIndexVersion: pins.retrievalIndex,
        traceVersion: pins.trace,
        evidenceIds: [...evidenceIds],
    });
    if (!parsed) throw new TypeError("Invalid assistant response delivery metadata.");
    return parsed;
}

export function createLegacyResponseDeliveryMetadata(
    responseModel: string = LEGACY_ASSISTANT_VERSION_PINS.responseModel,
): Readonly<AssistantResponseDeliveryMetadata> {
    return metadataFromPins(
        "legacy",
        "legacy_response",
        "legacy",
        { ...LEGACY_ASSISTANT_VERSION_PINS, responseModel },
        [],
    );
}

export function createAssistantV2ResponseDeliveryMetadata(input: Readonly<{
    responseKind: "navigation" | "final_response";
    task: Exclude<AssistantMeasuredTask, "legacy">;
    versionPins: AssistantGraphVersionPins;
    evidenceIds: readonly string[];
}>): Readonly<AssistantResponseDeliveryMetadata> {
    return metadataFromPins(
        "v2",
        input.responseKind,
        input.task,
        input.versionPins,
        input.evidenceIds,
    );
}

export function buildAssistantFeedbackInput(input: Readonly<{
    responseId: string;
    rating: AssistantFeedbackRating;
}>): Readonly<AssistantFeedbackInput> {
    const parsed = parseAssistantFeedbackInput({
        version: ASSISTANT_FEEDBACK_VERSION,
        responseId: input.responseId,
        rating: input.rating,
    });
    if (!parsed) throw new TypeError("Invalid assistant response feedback input.");
    return parsed;
}

export function summarizeFeedback(ratings: readonly AssistantFeedbackRating[]) {
    const helpful = ratings.filter((rating) => rating === "helpful").length;
    const notHelpful = ratings.filter((rating) => rating === "not_helpful").length;
    const total = helpful + notHelpful;
    return Object.freeze({
        total,
        helpful,
        notHelpful,
        helpfulRate: total === 0 ? null : helpful / total,
    });
}
